import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PlayCircle, AlertTriangle, Clock, Video, Monitor, Compass, TrendingDown } from "lucide-react";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatOffset(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Session Replay — watch back a completed classic or live interview's proctoring recording
 * (webcam/screen) synced with the AI's per-question feedback annotations. The seek offset for
 * each Q&A / violation is computed from real timestamps (answered_at vs. the proctoring
 * session's started_at baseline), not simulated — since the recording starts the moment the
 * candidate gives proctoring consent, which lines up with the interview session start.
 */
export default function ReplaySessionPage() {
  const { sessionType, sessionId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [session, setSession] = useState(null);
  const [proctoring, setProctoring] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [recordingKind, setRecordingKind] = useState("webcam");

  useEffect(() => {
    (async () => {
      try {
        const sessionReq = sessionType === "live" ? api.get(`/voice-interview/${sessionId}`) : api.get(`/interview/${sessionId}`);
        const [sessionRes, proctoringRes] = await Promise.allSettled([
          sessionReq,
          api.get(`/proctoring/session/by-session/${sessionId}`, { params: { session_type: sessionType } }),
        ]);
        if (sessionRes.status === "fulfilled") setSession(sessionRes.value.data);
        else setNotFound(true);
        if (proctoringRes.status === "fulfilled") {
          const p = proctoringRes.value.data;
          setProctoring(p);
          setRecordingKind(p.webcam_recording_url ? "webcam" : "screen");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionType, sessionId]);

  const startedAtMs = proctoring?.started_at ? new Date(proctoring.started_at).getTime() : null;
  const offsetFor = (isoTimestamp) => {
    if (!startedAtMs || !isoTimestamp) return null;
    const diff = (new Date(isoTimestamp).getTime() - startedAtMs) / 1000;
    return diff > 0 ? diff : 0;
  };

  const jumpTo = (seconds) => {
    if (videoRef.current && seconds !== null) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play().catch(() => {});
    }
  };

  const recordingUrl = recordingKind === "webcam" ? proctoring?.webcam_recording_url : proctoring?.screen_recording_url;
  const qaList = !session ? [] : sessionType === "live" ? (session.turns || []).filter((t) => t.answered) : (session.questions || []).filter((q) => q.answer);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="replay-loading">
        <div className="w-10 h-10 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !session) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="replay-not-found">
        <p className="text-slate-400">This interview session could not be found.</p>
        <button onClick={() => navigate("/interview")} data-testid="replay-not-found-back-btn" className="mt-4 text-indigo-400 text-sm font-semibold">Back to Interview Center</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="replay-session-page">
      <button onClick={() => navigate("/interview")} data-testid="replay-back-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Interview Center
      </button>
      <h1 className="font-heading text-2xl font-semibold text-white mb-1">Session Replay</h1>
      <p className="text-sm text-slate-400 mb-6 capitalize">{session.type} interview{session.mode === "panel" ? " • Panel Mode" : ""} — {new Date(session.completed_at || session.started_at).toLocaleString()}</p>

      {recordingUrl ? (
        <div className="glass-panel rounded-2xl p-4 mb-6" data-testid="replay-video-panel">
          <div className="flex items-center gap-2 mb-3">
            {proctoring?.webcam_recording_url && (
              <button
                onClick={() => setRecordingKind("webcam")} data-testid="replay-tab-webcam"
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${recordingKind === "webcam" ? "bg-indigo-500 text-white" : "bg-white/5 text-slate-400"}`}
              >
                <Video className="w-3.5 h-3.5" /> Webcam
              </button>
            )}
            {proctoring?.screen_recording_url && (
              <button
                onClick={() => setRecordingKind("screen")} data-testid="replay-tab-screen"
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${recordingKind === "screen" ? "bg-indigo-500 text-white" : "bg-white/5 text-slate-400"}`}
              >
                <Monitor className="w-3.5 h-3.5" /> Screen
              </button>
            )}
          </div>
          <video ref={videoRef} src={`${BACKEND_URL}${recordingUrl}`} controls className="w-full rounded-xl bg-black max-h-[420px]" data-testid="replay-video-player" />
        </div>
      ) : (
        <div className="glass-panel rounded-2xl p-6 mb-6 text-center text-sm text-slate-400" data-testid="replay-no-recording">
          No recording available for this session — you can still review the Q&amp;A and AI feedback below.
        </div>
      )}

      {session.result?.communication_coach && (
        <div className="glass-panel rounded-2xl p-5 mb-6 border border-violet-400/20" data-testid="replay-communication-coach-card">
          <p className="text-[11px] font-bold uppercase tracking-wider text-violet-400 flex items-center gap-1.5 mb-3">
            <Compass className="w-3.5 h-3.5" /> Review Pass — AI Communication Coach
          </p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-white/[0.03] rounded-lg p-3 text-center">
              <p className="text-2xl font-heading font-bold text-white" data-testid="replay-eye-contact-score">{session.result.communication_coach.eye_contact_score}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Eye Contact</p>
            </div>
            <div className="bg-white/[0.03] rounded-lg p-3 text-center">
              <p className="text-2xl font-heading font-bold text-white" data-testid="replay-confidence-score">{session.result.communication_coach.confidence_score}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Confidence &amp; Body Language</p>
            </div>
          </div>
          {session.result.communication_coach.notes?.length > 0 && (
            <ul className="space-y-1.5 mb-3">
              {session.result.communication_coach.notes.map((note, i) => (
                <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5"><span className="text-violet-400 mt-0.5">•</span> {note}</li>
              ))}
            </ul>
          )}
          {proctoring?.vision_coach_samples?.some((s) => s.eye_contact_score < 60 || s.confidence_score < 60) && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><TrendingDown className="w-3 h-3" /> Moments to Review</p>
              <div className="space-y-1.5">
                {proctoring.vision_coach_samples.filter((s) => s.eye_contact_score < 60 || s.confidence_score < 60).slice(0, 5).map((s, i) => {
                  const offset = offsetFor(s.timestamp);
                  return (
                    <div key={i} className="flex items-center gap-3 text-xs text-slate-300" data-testid={`replay-coach-moment-${i}`}>
                      <Clock className="w-3 h-3 text-slate-500 shrink-0" />
                      <span className="flex-1">{s.note || `Eye contact ${s.eye_contact_score}% · Confidence ${s.confidence_score}%`}</span>
                      {offset !== null && recordingUrl && (
                        <button onClick={() => jumpTo(offset)} data-testid={`replay-coach-jump-${i}`} className="text-indigo-300 hover:text-indigo-200 font-semibold shrink-0">Jump to {formatOffset(offset)}</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <p className="text-[10px] text-slate-500 mt-2.5">Based on {session.result.communication_coach.sample_count} webcam snapshots analyzed during the interview.</p>
        </div>
      )}

      <div className="space-y-4">
        {qaList.map((item, i) => {
          const answer = item.answer || item.sql_query || "";
          const offset = offsetFor(item.answered_at);
          return (
            <div key={i} className="glass-panel rounded-2xl p-5" data-testid={`replay-qa-card-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-white">{i + 1}. {item.question}{item.round && <span className="ml-2 text-[10px] font-bold uppercase text-indigo-300 bg-indigo-400/10 px-1.5 py-0.5 rounded-full">{item.round}</span>}</p>
                {offset !== null && recordingUrl && (
                  <button onClick={() => jumpTo(offset)} data-testid={`replay-jump-btn-${i}`} className="flex items-center gap-1 text-xs font-semibold text-indigo-300 hover:text-indigo-200 shrink-0">
                    <PlayCircle className="w-3.5 h-3.5" /> Jump to {formatOffset(offset)}
                  </button>
                )}
              </div>
              {answer && <p className="text-sm text-slate-300 mt-2 italic">"{answer}"</p>}
              {item.feedback && (
                <div className="mt-3 bg-white/5 border border-white/10 rounded-xl p-3 text-xs text-slate-300" data-testid={`replay-feedback-${i}`}>
                  <span className="text-indigo-300 font-semibold">AI Feedback: </span>{item.feedback}
                </div>
              )}
              {item.technical_accuracy !== undefined && (
                <div className="flex gap-3 mt-3 text-[11px] text-slate-400" data-testid={`replay-scores-${i}`}>
                  <span>Technical: {item.technical_accuracy}</span>
                  <span>Clarity: {item.clarity}</span>
                  <span>Structure: {item.structure}</span>
                  <span>Confidence: {item.confidence}</span>
                </div>
              )}
            </div>
          );
        })}
        {qaList.length === 0 && <p className="text-sm text-slate-400 text-center py-6" data-testid="replay-no-answers">No answered questions found for this session.</p>}
      </div>

      {proctoring?.violations?.length > 0 && (
        <div className="glass-panel rounded-2xl p-5 mt-6 border border-amber-400/20" data-testid="replay-violations-panel">
          <p className="text-xs font-bold uppercase text-amber-300 mb-3 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Proctoring Flags</p>
          <div className="space-y-2">
            {proctoring.violations.map((v, i) => {
              const offset = offsetFor(v.timestamp);
              return (
                <div key={i} className="flex items-center gap-3 text-xs text-slate-300" data-testid={`replay-violation-${i}`}>
                  <Clock className="w-3 h-3 text-slate-500 shrink-0" />
                  <span className="flex-1">{v.message || v.type}</span>
                  {offset !== null && recordingUrl && (
                    <button onClick={() => jumpTo(offset)} data-testid={`replay-violation-jump-${i}`} className="text-indigo-300 hover:text-indigo-200 font-semibold">Jump</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
